/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-debugger */
/* eslint-disable no-unused-expressions */
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import MyPaymentsActions from "../../../redux/reducers/payments.reducer";
import IssuerPaymentModel from "../../../lib/models/issuerPayment.model";
import {
  differenceForPayments,
  getAvailableBusinessDay,
  formatHolidays
} from "../../../lib/utils";

/**
 * Hook para manejar datos de pagos desde Redux
 * Centraliza toda la lógica de obtención y procesamiento de datos de pagos
 */
export const usePaymentData = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(MyPaymentsActions.startMyPayments());
  }, [dispatch]);

  // Selectores de Redux
  const paymentsSelector = useSelector(({ payments }) => payments);
  const userInfo = useSelector((state) => state.user.userInfo?.user);
  const userName = userInfo?.name || "";
  const userEmail = userInfo?.email || "";
  const userId = userInfo?.id || null;
  const otherAmount = useSelector((state) => state.payments.otherAmount);
  const showState = useSelector((state) => state.payments.showState);

  // Datos base
  const pendingPayments = paymentsSelector.issuerPaymentList || [];
  const payments = paymentsSelector.paymentsList || [];
  const holidays = paymentsSelector.holidays || [];
  const overduePayments = paymentsSelector.overduePayments || [];
  const isLoading = paymentsSelector.isLoading;

  // Generar opciones para mapping de campañas
  const campaigns = pendingPayments.map((item) => ({
    value: item.campaign_id,
    text: `${item.name}`
  }));

  /**
   * Procesa cada pago pendiente y calcula sus estados
   * Los pagos inactivos (state === "inactive" y total === 0) se marcan como completados
   */
  const processedPayments = useMemo(
    () =>
      pendingPayments.map((item) => {
        const {
          id,
          campaign_id: campaignId,
          issuer_payment: issuerPayment,
          name: campaignName
        } = item;

        const pymePaymentStateId = issuerPayment.pyme_payment_state_id;
        const issuerPaymentTypeID = issuerPayment.issuer_payment_type_id;
        const netAmount = issuerPayment.amount_requested;

        // Verificar si el pago está inactivo (sin pagos pendientes)
        const isInactivePayment =
          issuerPayment.state === "inactive" && issuerPayment.total === 0;

        // Calcular días restantes
        const daysLeft = issuerPayment.payment_expiration_date
          ? differenceForPayments(
              issuerPayment.payment_expiration_date.split(" ")[0],
              holidays
            )
          : null;

        const businessDaysToPay = daysLeft ? daysLeft.businessDays : null;

        // Solo está en mora si:
        // 1. Hay una fecha de vencimiento (payment_expiration_date existe)
        // 2. Los días hábiles son negativos (ya pasó la fecha)
        // 3. El issuer_payment está activo (state !== "inactive" y total > 0)
        // 4. O el estado pyme_payment_state_id es EN_MORA
        // const hasActivePayment =
        //   issuerPayment.state !== "inactive" && issuerPayment.total > 0;

        const isOverdue =
          pymePaymentStateId ===
            IssuerPaymentModel.pymePaymentStateId.EN_MORA &&
          (issuerPaymentTypeID === IssuerPaymentModel.typeId.PAGO_TOTAL_MORA ||
            issuerPaymentTypeID ===
              IssuerPaymentModel.typeId.PAGO_PARCIAL_MORA);

        // Obtener información de mora
        const overduePayment = isOverdue
          ? overduePayments.find(
              (overdue) => Number(overdue.campaignId) === campaignId
            )
          : null;

        const overdueTotal = netAmount || overduePayment?.totalOverdue || 0;

        // Obtener fecha de vencimiento original del issuer_payment
        // En mora o no, siempre mostramos la fecha de vencimiento del pago
        let dateExpiration = issuerPayment.payment_expiration_date;

        // Ajustar fecha al siguiente día hábil
        if (dateExpiration) {
          const holidayListFormatted = formatHolidays(holidays);
          dateExpiration = getAvailableBusinessDay(
            dateExpiration,
            holidayListFormatted
          );
        }

        // Determinar estado de transacción basado en issuer_payment_state_id
        let transactionStatus = null;
        let transactionStatusLabel = null;

        // Detectar si hay un pago parcial procesándose.
        // Debe basarse en issuer_payment_type_id para evitar mensajes erróneos
        // cuando el transaction_type histórico no coincide con el tipo real del pago.
        const isPartialPayment =
          issuerPaymentTypeID === IssuerPaymentModel.typeId.PAGO_PARCIAL_MORA &&
          (issuerPayment.issuer_payment_state_id ===
            IssuerPaymentModel.stateId.APROBADO ||
            issuerPayment.issuer_payment_state_id ===
              IssuerPaymentModel.stateId.NOTIFICADO ||
            issuerPayment.issuer_payment_state_id ===
              IssuerPaymentModel.stateId.ACREDITADO);

        // A2CEN-5363: acreditación pendiente al balance de inversionistas.
        // Ortogonal al mapeo de 11 estados legacy — no lo reemplaza ni lo toca.
        // Fail-safe consistente con el backend: null/undefined se trata como pendiente (true).
        const isPendingAccreditation =
          issuerPayment.pending_accreditation !== false;

        // Si el pago ya fue aprobado, notificado o acreditado
        // O si el pago está inactivo (sin pagos pendientes)
        // O si está "Al día" (pyme_payment_state_id = 1) sin pago pendiente en franja
        // Mostrar el estado vacío "No tienes pagos pendientes"
        //
        // Criterio de negocio para mostrar tarjeta de pago:
        // - pyme_payment_state_id === 2 (Pendiente - En franja de pago)
        // - issuer_payment_type_id === 3 (Pago cuota ordinaria)
        // - issuer_payment_state_id === 1 (Pendiente)
        const isUpToDate =
          pymePaymentStateId === IssuerPaymentModel.pymePaymentStateId.AL_DIA;

        const isFinalizedPaymentCampaign =
          pymePaymentStateId ===
          IssuerPaymentModel.pymePaymentStateId.PAGOS_FINALIZADOS;

        const isGuaranteeInExecution =
          pymePaymentStateId ===
          IssuerPaymentModel.pymePaymentStateId.GARANTIA_EN_EJECUCION;

        const isGuaranteeExecuted =
          pymePaymentStateId ===
          IssuerPaymentModel.pymePaymentStateId.GARANTIA_EJECUTADA;

        const isPaymentCompleted =
          isInactivePayment ||
          isUpToDate ||
          isFinalizedPaymentCampaign ||
          isGuaranteeInExecution ||
          isGuaranteeExecuted ||
          (!isPartialPayment &&
            (issuerPayment.issuer_payment_state_id ===
              IssuerPaymentModel.stateId.APROBADO ||
              issuerPayment.issuer_payment_state_id ===
                IssuerPaymentModel.stateId.NOTIFICADO ||
              issuerPayment.issuer_payment_state_id ===
                IssuerPaymentModel.stateId.ACREDITADO));

        if (
          issuerPayment.issuer_payment_state_id ===
          IssuerPaymentModel.stateId.POR_CONFIRMAR
        ) {
          transactionStatus = "pending";
          transactionStatusLabel = "Por confirmar";
        } else if (
          issuerPayment.issuer_payment_state_id ===
          IssuerPaymentModel.stateId.RECHAZADO
        ) {
          transactionStatus = "rejected";
          transactionStatusLabel = "Rechazada";
        } else if (
          issuerPayment.issuer_payment_state_id ===
            IssuerPaymentModel.stateId.APROBADO &&
          !isPartialPayment
        ) {
          transactionStatus = "approved";
          transactionStatusLabel = "Aprobada";
        }

        const campaignData = {
          campaign_id: campaignId,
          name: campaignName,
          user_id: userId,
          user_email: userEmail
        };

        return {
          id,
          campaign: campaignData,
          issuerPayment,
          isOverdue,
          overdueTotal,
          dateExpiration,
          businessDaysToPay,
          transactionStatus,
          transactionStatusLabel,
          isPaymentCompleted,
          isPartialPayment,
          isPendingAccreditation,
          isFinalizedPaymentCampaign,
          isGuaranteeInExecution,
          isGuaranteeExecuted,
          netAmount,
          collectionCommission: overduePayment?.collectionCommission || 0,
          fngCommission: overduePayment?.overduePayments?.fngCommission || 0,
          overdueInterest: overduePayment?.overduePayments?.totalOverdueInterest || 0,
          overdueInterestRetention: overduePayment?.overduePayments?.totalOverdueRetention || 0,
          collectionRate: overduePayment?.overduePayments?.totalOverduePayCollectionPercentage || 0
        };
      }),
    [pendingPayments, holidays, overduePayments, userId, userEmail]
  );

  return {
    pendingPayments,
    payments,
    holidays,
    overduePayments,
    isLoading,
    userName,
    otherAmount,
    showState,
    campaigns,
    processedPayments,
    dispatch
  };
};
